<template>
  <div class="lang" :class="[tone, { open }]">
    <button
      type="button"
      class="lang-btn"
      :aria-expanded="open ? 'true' : 'false'"
      aria-haspopup="listbox"
      :aria-label="'Language: ' + currentLabel"
      @click.stop="open = !open"
    >
      <svg class="globe" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.6" />
        <path d="M3.5 12h17M12 3c2.6 2.4 4 5.6 4 9s-1.4 6.6-4 9c-2.6-2.4-4-5.6-4-9s1.4-6.6 4-9z" fill="none" stroke="currentColor" stroke-width="1.6" />
      </svg>
      <span class="lang-code">{{ current }}</span>
      <svg class="chevron" viewBox="0 0 12 8" aria-hidden="true">
        <path d="M1 1.5l5 5 5-5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
      </svg>
    </button>
    <div v-if="open" class="lang-menu" role="listbox">
      <button
        v-for="item in items"
        :key="item.code"
        type="button"
        role="option"
        :aria-selected="item.code === selected"
        :class="{ on: item.code === selected }"
        @click="pick(item.code)"
      >
        <span class="code">{{ item.code.toUpperCase() }}</span>
        <span class="name">{{ item.label }}</span>
        <svg v-if="item.code === selected" class="tick" viewBox="0 0 16 16" aria-hidden="true">
          <path d="M3 8.2l3.2 3.3L13 4.6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
const LABELS = {
  en: 'English',
  ar: 'العربية',
  fr: 'Français',
  es: 'Español',
}

export default {
  name: 'LanguageSwitch',
  props: {
    tone: {
      type: String,
      default: 'on-dark',
    },
  },
  data() {
    return {
      open: false,
      selected: 'en',
    }
  },
  computed: {
    current() {
      return (this.selected || 'en').toUpperCase()
    },
    currentLabel() {
      return LABELS[this.selected] || this.current
    },
    items() {
      const codes = this.$availableLanguages || ['en', 'ar', 'fr', 'es']
      return codes.map((code) => ({ code, label: LABELS[code] || code.toUpperCase() }))
    },
  },
  mounted() {
    this.selected = localStorage.getItem('lang') || this.$i18n.locale || 'en'
    this.$setDocumentLangDir(this.selected)
    this.onDocClick = (e) => {
      if (!this.$el.contains(e.target)) this.open = false
    }
    document.addEventListener('click', this.onDocClick)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocClick)
  },
  methods: {
    pick(lang) {
      this.selected = lang
      this.$i18n.locale = lang
      this.$setDocumentLangDir(lang)
      localStorage.setItem('lang', lang)
      this.open = false
    },
  },
}
</script>

<style scoped>
.lang { position: relative; }
.lang-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 42px;
  padding: 0 6px 0 4px;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: .06em;
  cursor: pointer;
  border-radius: 10px;
}
.globe { width: 16px; height: 16px; opacity: .9; }
.chevron { width: 9px; height: 6px; opacity: .7; transition: transform .18s ease; }
.lang.open .chevron { transform: rotate(180deg); }
.lang.on-dark .lang-btn { color: rgba(255, 255, 255, .82); }
.lang.on-dark .lang-btn:hover,
.lang.on-dark.open .lang-btn { color: var(--gold); }
.lang.on-light .lang-btn { color: var(--navy); }
.lang-menu {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  min-width: 220px;
  padding: 8px;
  background: #fff;
  border: 1px solid rgba(0, 40, 85, .06);
  border-radius: 14px;
  box-shadow: 0 20px 48px rgba(0, 40, 85, .18);
  z-index: 40;
}
.lang-menu button {
  display: grid;
  grid-template-columns: 28px 1fr 16px;
  align-items: center;
  gap: 10px;
  width: 100%;
  border: 0;
  background: transparent;
  padding: 10px 12px;
  border-radius: 10px;
  font: inherit;
  font-weight: 500;
  color: var(--navy);
  cursor: pointer;
  text-align: left;
}
.lang-menu button .code {
  color: #6B7280;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .06em;
}
.lang-menu button .name { font-size: 14px; }
.lang-menu button .tick { width: 16px; height: 16px; color: var(--gold); }
.lang-menu button.on { background: var(--canvas); }
.lang-menu button.on .code { color: var(--gold); }
.lang-menu button:hover { background: #EEF3F8; }
</style>
